(ns storrito.instagram.story.editor.model
  (:require [storrito.image :as image]
            [storrito.editor.background :as bg]
            [storrito.video :as video]
            [storrito.instagram.story.editor.dimension :as dimension]
            [storrito.fabricjs.canvas-data :as canvas-data]
            [cljs.core.async :as async :refer [<!]]
            ["fabric" :refer [fabric]]
            [storrito.fabricjs.object-data :as object-data]
            [storrito.fabricjs.center :as center]
            [storrito.video.config :as video-config]
            [storrito.util :as util])
  (:require-macros [cljs.core.async.macros :refer [go]]))

(def preview-side-length 800)

(def default-background-color "#000000")

(def overlay-id "storrito-tag")

(def default-model-data
  (let [zoom (/ 1 3)
        image-size dimension/story-image
        bg-properties (bg/background-color-rect {:fill default-background-color
                                      :margin 0})
        bc (canvas-data/canvas-object->clj bg-properties)]
    {:canvas {:objects (into {}
                             (map (juxt :id identity))
                             [bc])}
     ;; Default values when a gif is added to an image.
     ;; In case of a video, these values will be overwritten:
     :duration video-config/ig-image-gif-duration
     :start-time 0
     :end-time video-config/ig-image-gif-duration
     :total-length video-config/ig-image-gif-duration

     :canvas/hide-margins? false
     :dimension/image-size image-size
     :canvas/zoom zoom
     :dimension/zoom zoom
     :location/sticker-type :default-rainbow
     :instagram.overlay/visible? true
     :object-snapping? true}))

(def default-video-model-data default-model-data)

(defn new-image-object
  "Creates new fabricjs Image object. Requires params for that object."
  [params]
  (let [default-params {:id (:id params)
                        :width (:width params)
                        :height (:height params)
                        :crossOrigin "anonymous"}
        selectable? (some? (:selectable params))
        fabric-Image #(object-data/image %)
        add-selectable #(assoc % :selectable (:selectable params))
        add-src #(assoc % :src (:image-url params))]
    (cond->
      default-params
      selectable? add-selectable
      true fabric-Image
      ;; Add the `:src` after the FabricJS Image constructor to avoid
      ;; preloading:
      true add-src)))

(defn new-video-object
  "Creates a new FabricJS object that represents a video."
  [params]
  (-> {:id (:id params)
       :width (:width params)
       :height (:height params)
       :crossOrigin "anonymous"}
      (object-data/image)
      ;; `src` needs to be removed here or videos can be interpreted as
      ;; images by fabricjs when hitting undo
      (dissoc :src)
      (assoc :video_src (:video-url params))))

(defn add-object
  "Adds the given object to the FabricsJS canvas data in the model
   value."
  [model-value object-data]
  (assoc-in model-value
            [:canvas :objects (:id object-data)]
            object-data))

(defn fit-object
  "Adapts the scale and position of the object, so that it fits into the
   size of the canvas."
  [object-data canvas-size]
  (let [scaled-size (image/fill (select-keys object-data
                                             [:width
                                              :height])
                                canvas-size)]
    (merge
      object-data
      {:scaleX (:scale-factor scaled-size)
       :scaleY (:scale-factor scaled-size)
       :top (- (/ (:height canvas-size) 2.0)
               (/ (:height scaled-size) 2.0))})))

(defn fill-background
  "Adapts the scale and the position of the object, so that it fills the
   complete background of the canvas."
  [object-data canvas-size]
  (let [scaled-size (image/fill-background (select-keys object-data
                                                        [:width
                                                         :height])
                                           canvas-size)]
    (assoc object-data
           :scaleX (:scale-factor scaled-size)
           :scaleY (:scale-factor scaled-size)
           :top (- (/ (:height canvas-size) 2.0)
                   (/ (:height scaled-size) 2.0))
           :left (- (/ (:width canvas-size) 2.0)
                    (/ (:width scaled-size) 2.0))
           :width (:width scaled-size)
           :height (:height scaled-size))))

(defn new-blurry-background
  "Returns the FabricJS object data for a blurry background image."
  [params]
  (-> (select-keys params
                   [:width
                    :height
                    :image-url])
      (assoc :id "background")
      (assoc :selectable false)
      (new-image-object)
      (assoc :filters [{:type "Blur"
                        :blur 1.0}])))

(defn make-quick-story-model [story-size
                              {:background/keys [blob size constrain-fn]}]
  (assert blob)
  (-> default-model-data
      (add-object (-> (merge size
                             {:id "main-image"
                              :image-url (image/url blob)})
                      (new-image-object)
                      (fit-object story-size)))
      (add-object (-> size
                      (assoc :image-url (image/url blob))
                      (new-blurry-background)
                      (fill-background story-size)))
      :canvas))

(defn prepare-video-metadata
  [entity video-metadata default-model-value]
  (let [model-value default-model-value
        story-size (:dimension/image-size model-value)
        video-width (::video/width video-metadata)
        video-height (::video/height video-metadata)
        start-time (:instagram.video/start-time entity 0)
        entity-duration (:instagram.video/duration entity)
        src-video-duration (:storrito.video/duration video-metadata)
        end-time (+ start-time
                    (util/clamp
                      (or entity-duration
                          ;; use one item as default length:
                          (min video-config/ig-story-max-duration
                               ;; or the length of the original video
                               ;; if it is shorter than one item:
                               src-video-duration))
                      video-config/ig-story-min-duration
                      video-config/max-duration))
        video-url (video/url (:story-media/blob-id entity))]
    (-> model-value
        (add-object (-> {:id "main-video"
                         :width video-width
                         :height video-height
                         :video-url video-url}
                        (new-video-object)
                        (fit-object story-size)))
        (update-in [:canvas :order]
                   conj "main-video")
        (assoc :video-metadata video-metadata
               :start-time start-time
               :end-time end-time
               :total-length src-video-duration
               :muted true))))

(def default-video-metadata
  ;; for story images that contains gifs:
  {:storrito.video/duration video-config/ig-image-gif-duration
   :storrito.video/width 1080
   :storrito.video/height 1920})

(defn get-video-metadata
  "Get the metadata of the main-video, if the `story-media` has
   one. Otherwise it returns the `default-video-metadata`."
  [story-media]
  (go
    (if-let [video-blob-id (:story-media/blob-id story-media)]
      (<! (video/metadata (video/url video-blob-id)))
      default-video-metadata)))

(defn video-model+metadata
  [story-media default-model-value]
  (go
    (let [video-metadata (<! (get-video-metadata story-media))]
      (prepare-video-metadata story-media
                              video-metadata
                              default-model-value))))
